import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Settings2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductConfigurator = ({ product, onConfigChange, onAddToCart }) => {
  const [config, setConfig] = useState(null);
  const [selections, setSelections] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, [product.id]);

  useEffect(() => {
    if (Object.keys(selections).length > 0) {
      calculatePrice();
    }
  }, [selections]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/${product.id}/config`);
      setConfig(res.data);
      
      // Set default selections
      if (res.data.configurable && res.data.options) {
        const defaults = {};
        Object.keys(res.data.options).forEach(key => {
          const options = res.data.options[key].options;
          if (options && options.length > 0) {
            defaults[key] = options[0].value;
          }
        });
        setSelections(defaults);
      }
    } catch (e) {
      console.error('Error fetching config:', e);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/products/${product.id}/calculate-price`, selections);
      setCalculatedPrice(res.data);
      if (onConfigChange) {
        onConfigChange(res.data);
      }
    } catch (e) {
      console.error('Error calculating price:', e);
    }
  };

  const handleSelect = (optionType, value) => {
    setSelections(prev => ({
      ...prev,
      [optionType]: value
    }));
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-2xl p-6 h-48"></div>
    );
  }

  if (!config?.configurable) {
    return null;
  }

  return (
    <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-6" data-testid="product-configurator">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 className="h-5 w-5 text-[#3A5A40]" />
        <h3 className="font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
          Configure Your {product.name}
        </h3>
      </div>

      <div className="space-y-6">
        {Object.entries(config.options).map(([optionType, optionData]) => (
          <div key={optionType}>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {optionData.label}
            </label>
            <div className="grid grid-cols-1 gap-3">
              {optionData.options.map((option) => {
                const isSelected = selections[optionType] === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(optionType, option.value)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'border-[#3A5A40] bg-white shadow-sm' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    data-testid={`config-option-${optionType}-${option.value}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-[#3A5A40] bg-[#3A5A40]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className={`font-medium ${isSelected ? 'text-[#1A1A1A]' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                    </div>
                    {option.price_modifier > 0 && (
                      <span className={`text-sm ${isSelected ? 'text-[#3A5A40] font-semibold' : 'text-gray-500'}`}>
                        +${option.price_modifier.toLocaleString()}
                      </span>
                    )}
                    {option.price_modifier === 0 && (
                      <span className="text-sm text-gray-400">Included</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Price Summary */}
      {calculatedPrice && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">Base Price</span>
            <span className="font-medium">${calculatedPrice.base_price.toLocaleString()}</span>
          </div>
          {calculatedPrice.price_modifier > 0 && (
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-gray-500">Configuration</span>
              <span className="text-[#3A5A40]">+${calculatedPrice.price_modifier.toLocaleString()}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-lg font-bold text-[#1A1A1A]">Your Price</span>
            <span className="text-2xl font-bold text-[#3A5A40]" data-testid="configured-price">
              ${calculatedPrice.total_price.toLocaleString()}
            </span>
          </div>

          {/* Selected Options Summary */}
          <div className="mt-4 p-3 bg-white rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Your Configuration:</p>
            <div className="flex flex-wrap gap-2">
              {calculatedPrice.selected_options.map((opt, i) => (
                <span key={i} className="px-2 py-1 bg-[#E0F2F1] text-[#3A5A40] text-xs rounded-full">
                  {opt.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductConfigurator;
